import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { makeAsk, ERRORS, Card, Button, Notice, Gate, since, channelOptions, asList, TabBoundary } from './dashboardShared';
import FeedsTab from './FeedsTab';
import GiveawayTab from './GiveawayTab';
import EmbedBuilder from './EmbedBuilder';
import ComponentsBuilder from './ComponentsBuilder';
import CommandsTab from './CommandsTab';
import GatekeepTab from './GatekeepTab';
import PartnersTab from './PartnersTab';
import WebsiteTab from './WebsiteTab';
import ServerTab from './ServerTab';
import { MembersPanel } from './ModDashboard';

// #dashboard: the TOGΞ Dashboard, for the bot owner. Opened from the username menu in the nav.
// Every request goes to the bot (the dashboard cog), which checks the Discord login belongs to the bot owner itself.

// Most used first: the bot itself and who's in the server, then what TOGΞ posts, then giveaways, then the website's
// own settings and the server's setup, then partners and which servers TOGΞ is in, then the tools you reach for now and then
const TABS = [
  ['overview', 'Overview'], ['members', 'Members'], ['feeds', 'Feeds'], ['giveaway', 'Giveaway'],
  ['website', 'Website'], ['server', 'Server'],
  ['partners', 'Partners'], ['gatekeep', 'Gatekeep'], ['embed', 'Embed'], ['commands', 'Commands'],
];

export default function Dashboard({ database, discordUser, isAdmin, getToken, onLogin }) {
  // The same `ask` for the life of the page (the site redraws every second for its clock, with a new getToken each
  // time: a new ask each time would make the tabs reload from the bot every second)
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const ask = useMemo(() => makeAsk(database, () => tokenRef.current()), [database]);
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [problem, setProblem] = useState(null); // a key of ERRORS
  const [lists, setLists] = useState({ channels: [], roles: [] });
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState(null);       // { text, ok } after an action
  const [confirm, setConfirm] = useState('');
  const [restarting, setRestarting] = useState(null); // when a restart was asked for
  const [embedMode, setEmbedMode] = useState(() => {
    try { return localStorage.getItem('dashboardEmbedMode') || 'classic'; } catch { return 'classic'; }
  });
  const [, setTick] = useState(0);

  // An answer that went wrong: the login / access ones change the whole page, the rest show as a red note
  const failed = useCallback((answer) => {
    if (answer.ok) return false;
    if (ERRORS[answer.error]) setProblem(answer.error);
    else setNote({ text: answer.error, ok: false });
    return true;
  }, []);

  const loadOverview = useCallback(async () => {
    const answer = await ask('overview');
    // Every list filled in (Firebase drops empty ones)
    if (answer.ok) { setOverview({ ...answer.data, cogs: asList(answer.data?.cogs), recent: asList(answer.data?.recent) }); setProblem(null); return true; }
    if (ERRORS[answer.error]) setProblem(answer.error);
    return false;
  }, [ask]);

  useEffect(() => {
    if (!isAdmin || !getToken()) return;
    loadOverview();
    ask('channels').then((answer) => {
      if (answer.ok) setLists({ channels: asList(answer.data?.channels), roles: asList(answer.data?.roles) });
    });
    const uptime = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(uptime);
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a restart: ask every few seconds until TOGΞ answers again (it gives up after two minutes)
  useEffect(() => {
    if (!restarting) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      if (Date.now() - restarting > 120000) {
        setRestarting(null);
        setNote({ text: "TOGΞ hasn't come back after two minutes. Check the VPS: journalctl -u togebot -n 50", ok: false });
        return;
      }
      const answer = await ask('overview', {}, 4000);
      if (stopped) return;
      if (answer.ok && answer.data.startedAt > restarting - 5000) {
        setOverview({ ...answer.data, cogs: asList(answer.data.cogs), recent: asList(answer.data.recent) });
        setProblem(null); setRestarting(null);
        setNote({ text: '✅ TOGΞ is back online.', ok: true });
      } else setTimeout(poll, 3000);
    };
    const first = setTimeout(poll, 8000); // give it time to shut down first
    return () => { stopped = true; clearTimeout(first); };
  }, [restarting]); // eslint-disable-line react-hooks/exhaustive-deps

  const cogAction = async (verb, name) => {
    if (verb === 'unload' && confirm !== `unload-${name}`) { setConfirm(`unload-${name}`); return; }
    setConfirm(''); setBusy(`${verb}-${name}`); setNote(null);
    const answer = await ask(`cog.${verb}`, { name });
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: answer.data.message, ok: true });
    loadOverview();
  };

  const restart = async () => {
    if (confirm !== 'restart') { setConfirm('restart'); return; }
    setConfirm(''); setBusy('restart'); setNote(null);
    const answer = await ask('bot.restart');
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: answer.data.message, ok: true });
    setOverview(null);
    setRestarting(Date.now());
  };

  if (!discordUser) return <Gate title="TOGΞ Dashboard" text="Log in with Discord to use the dashboard." onLogin={onLogin} />;
  if (!isAdmin || problem === 'forbidden') return <Gate title="TOGΞ Dashboard" text={ERRORS.forbidden} />;
  if (problem === 'login' || !getToken()) return <Gate title="TOGΞ Dashboard" text={ERRORS.login} onLogin={onLogin} />;

  const wide = tab === 'embed';
  const statusText = restarting ? 'Restarting… waiting for TOGΞ to come back'
    : overview ? `Online${overview.latency != null ? ` · ${overview.latency} ms` : ''}` : problem ? ERRORS[problem] : 'Connecting to the bot…';
  const shared = { ask, lists, setNote, failed };

  return (
    <main className="flex-1">
      <div className={`${wide ? 'max-w-6xl' : 'max-w-3xl'} mx-auto px-4 pt-2 pb-10`}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          {overview?.bot?.avatar
            ? <img src={overview.bot.avatar} alt="" className="h-10 w-10 rounded-full" />
            : <div className="h-10 w-10 rounded-full bg-zinc-800" />}
          <div className="min-w-0 flex-1">
            <h1 className="text-white text-lg font-bold leading-tight">TOGΞ Dashboard</h1>
            <p className="text-[11px] flex items-center gap-1.5">
              <span className={`inline-block h-2 w-2 rounded-full ${restarting ? 'bg-amber-400 animate-pulse' : overview ? 'bg-[#23a55a]' : problem ? 'bg-red-500' : 'bg-zinc-500'}`} />
              <span className="text-zinc-400 truncate">{statusText}</span>
            </p>
          </div>
          <Button tone="ghost" small onClick={loadOverview} disabled={!!restarting}>↻ Refresh</Button>
        </div>

        {/* Tabs: wrap onto a second line when there isn't room, rather than scrolling */}
        <div className="mb-4">
          <div className="inline-flex flex-wrap gap-1 p-1 rounded-lg bg-zinc-900 border border-zinc-800 max-w-full">
            {TABS.map(([id, label]) => (
              <button key={id} onClick={() => { setTab(id); setNote(null); setConfirm(''); }}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors whitespace-nowrap ${tab === id ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <Notice note={note} />

        {tab === 'overview' && (
          <div className="space-y-4">
            {/* Three across on every screen */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[['Status', restarting ? 'Restarting' : overview ? 'Online' : '—', overview?.latency != null ? `${overview.latency} ms` : ''],
                ['Up for', overview ? since(overview.startedAt) : '—', overview ? `since ${new Date(overview.startedAt).toLocaleDateString()}` : ''],
                ['Members', overview?.guild?.members?.toLocaleString() ?? '—', overview?.guild?.name || '']].map(([label, value, sub]) => (
                <Card key={label} className="!p-2.5 sm:!p-4">
                  <p className="text-zinc-500 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate">{label}</p>
                  <p className="text-white text-base sm:text-xl font-bold truncate">{value}</p>
                  <p className="text-zinc-500 text-[10px] sm:text-[11px] truncate">{sub || ' '}</p>
                </Card>
              ))}
            </div>

            <Card title="The bot" right={
              <Button small tone={confirm === 'restart' ? 'danger' : 'plain'} onClick={restart} disabled={!!busy || !!restarting || !overview}>
                {busy === 'restart' ? '…' : restarting ? 'Restarting…' : confirm === 'restart' ? 'Yes, restart TOGΞ' : '🔄 Restart bot'}
              </Button>
            }>
              <p className="text-zinc-400 text-xs">
                Restarting stops TOGΞ and its service starts it again straight away: it's offline until it's back up,
                and this page says when. Use it after updating files in core/ (cogs only need a reload).
              </p>
            </Card>

            <Card title={overview ? `Cogs · ${overview.cogs.filter(c => c.loaded).length} of ${overview.cogs.length} loaded` : 'Cogs'}>
              {!overview ? <p className="text-zinc-500 text-xs">{restarting ? 'Waiting for TOGΞ…' : 'Loading…'}</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {overview.cogs.map(({ name, loaded }) => (
                    <div key={name} className="flex items-center gap-2 rounded-lg bg-zinc-950/60 border border-zinc-800 px-3 py-2">
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${loaded ? 'bg-[#23a55a]' : 'bg-zinc-600'}`} />
                      <span className={`text-xs font-semibold flex-1 truncate ${loaded ? 'text-white' : 'text-zinc-500'}`}>{name}</span>
                      {loaded ? (
                        <>
                          <Button small onClick={() => cogAction('reload', name)} disabled={!!busy}>{busy === `reload-${name}` ? '…' : 'Reload'}</Button>
                          {name !== 'dashboard' && (
                            <Button small tone={confirm === `unload-${name}` ? 'danger' : 'ghost'} onClick={() => cogAction('unload', name)} disabled={!!busy}>
                              {busy === `unload-${name}` ? '…' : confirm === `unload-${name}` ? 'Sure?' : 'Unload'}
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button small tone="primary" onClick={() => cogAction('load', name)} disabled={!!busy}>{busy === `load-${name}` ? '…' : 'Load'}</Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {overview?.recent?.length > 0 && (
              <Card title="Recent dashboard requests (since the cog started)">
                <ul className="space-y-1">
                  {[...overview.recent].reverse().map((line, i) => (
                    <li key={i} className="text-zinc-400 text-[11px] font-mono">
                      {line.replace(/<t:(\d+):T>/, (_, s) => new Date(s * 1000).toLocaleTimeString())}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        )}

        <TabBoundary key={tab}>
        {tab === 'members' && <MembersPanel ask={ask} failed={failed} />}
        {tab === 'feeds' && <FeedsTab {...shared} />}
        {tab === 'giveaway' && <GiveawayTab {...shared} />}
        {tab === 'website' && <WebsiteTab {...shared} database={database} />}
        {tab === 'server' && <ServerTab {...shared} />}
        {tab === 'commands' && <CommandsTab {...shared} />}
        {tab === 'gatekeep' && <GatekeepTab {...shared} />}
        {tab === 'partners' && <PartnersTab {...shared} />}

        {tab === 'embed' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex gap-1 p-1 rounded-lg bg-zinc-900 border border-zinc-800 w-fit">
                {[['classic', 'Classic embed'], ['components', 'Components']].map(([id, label]) => (
                  <button key={id} onClick={() => { setEmbedMode(id); try { localStorage.setItem('dashboardEmbedMode', id); } catch {} }}
                    className={`px-3 py-1 rounded-md text-xs font-semibold ${embedMode === id ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-zinc-500 text-[11px]">
                {embedMode === 'classic' ? 'The familiar embed: title, fields, image, footer.'
                  : 'Discord’s newer layout: boxes, text, sections with pictures or buttons, galleries and dividers, in any order.'}
              </p>
            </div>
            {embedMode === 'classic'
              ? <EmbedBuilder ask={ask} lists={lists} bot={overview?.bot} channelOptions={() => channelOptions(lists.channels)} />
              : <ComponentsBuilder ask={ask} lists={lists} bot={overview?.bot} channelOptions={() => channelOptions(lists.channels)} />}
          </div>
        )}
        </TabBoundary>
      </div>
    </main>
  );
}
